import { Slider as BaseSlider } from "@base-ui/react/slider";
import type React from "react";
import { type CSSProperties, forwardRef } from "react";
import {
  sliderControlClass,
  sliderIndicatorClass,
  sliderLabelClass,
  sliderRootClass,
  sliderThumbClass,
  sliderTrackClass,
  sliderValueClass,
} from "./styles.css";

export type SliderOrientation = "horizontal" | "vertical";

interface SliderProps {
  value?: number | number[];
  defaultValue?: number | number[];
  onValueChange?: (value: number | number[]) => void;
  onValueCommitted?: (value: number | number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  orientation?: SliderOrientation;
  name?: string;
  label?: React.ReactNode;
  showValue?: boolean;
  className?: string;
  style?: CSSProperties;
}

export const Slider = forwardRef<HTMLDivElement, SliderProps>(function Slider(
  {
    value,
    defaultValue = 0,
    onValueChange,
    onValueCommitted,
    min = 0,
    max = 100,
    step = 1,
    disabled,
    orientation = "horizontal",
    name,
    label,
    showValue,
    className,
    style,
  },
  ref,
) {
  const values = Array.isArray(value)
    ? value
    : value !== undefined
      ? [value]
      : undefined;
  const defaultValues = Array.isArray(defaultValue)
    ? defaultValue
    : [defaultValue];
  const thumbCount = values?.length ?? defaultValues.length;

  return (
    <BaseSlider.Root
      ref={ref}
      value={values}
      defaultValue={defaultValues}
      onValueChange={onValueChange}
      onValueCommitted={onValueCommitted}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      orientation={orientation}
      name={name}
      className={`${sliderRootClass}${className ? ` ${className}` : ""}`}
      style={style}
    >
      {label && (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <BaseSlider.Label className={sliderLabelClass}>
            {label}
          </BaseSlider.Label>
          {showValue && <BaseSlider.Value className={sliderValueClass} />}
        </div>
      )}
      <BaseSlider.Control className={sliderControlClass}>
        <BaseSlider.Track className={sliderTrackClass}>
          <BaseSlider.Indicator className={sliderIndicatorClass} />
          {Array.from({ length: thumbCount }).map((_, i) => (
            <BaseSlider.Thumb key={i} className={sliderThumbClass} />
          ))}
        </BaseSlider.Track>
      </BaseSlider.Control>
    </BaseSlider.Root>
  );
});

Slider.displayName = "Slider";
